/*
 * SchemaPicker
 * The reader picks a page type and gets the structured-data types that fit it, plus a JSON-LD
 * starter with [placeholders] to replace.
 *
 * Rules:
 * - One page type at a time. Each maps to fixed types, notes and a starter snippet:
 *     practice page -> LegalService + BreadcrumbList
 *     attorney bio  -> Person (worksFor the firm) + BreadcrumbList
 *                      (schema.org marks the "Attorney" type as deprecated in favour of LegalService)
 *     location page -> LegalService with address, telephone, opening hours, areaServed
 *     page with FAQs -> FAQPage (questions and answers must match what's visible on the page;
 *                      Google only shows FAQ rich results for well-known government and health sites)
 *     blog post / nested page -> BlogPosting + BreadcrumbList
 * - "Copy" uses the clipboard only when clicked; if that fails, the reader is told to select the
 *   text manually. No tracking, storage or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type PageKey = "practice" | "bio" | "location" | "faq" | "post";

type Option = {
  key: PageKey;
  label: string;
  types: string[];
  notes: string[];
  snippet: string;
};

const OPTIONS: Option[] = [
  {
    key: "practice",
    label: "Practice-area page",
    types: ["LegalService", "BreadcrumbList"],
    notes: [
      "Describe the service the page is about, and keep it consistent with what the page says.",
      "Add BreadcrumbList when the page sits under another page (e.g. Practice areas › Car accidents).",
    ],
    snippet: `{
  "@context": "https://schema.org",
  "@type": "LegalService",
  "name": "[Firm name]",
  "url": "[https://yourfirm.com/practice-page]",
  "description": "[One sentence on what this practice handles]",
  "telephone": "[+1-000-000-0000]",
  "areaServed": "[City, State]",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "[Street]",
    "addressLocality": "[City]",
    "addressRegion": "[State]",
    "postalCode": "[ZIP]",
    "addressCountry": "US"
  }
}`,
  },
  {
    key: "bio",
    label: "Attorney bio",
    types: ["Person", "BreadcrumbList"],
    notes: [
      "Use Person for the attorney. schema.org marks the older “Attorney” type as deprecated in favour of LegalService.",
      "Link the attorney to the firm with worksFor, and list profiles (state bar, directories) in sameAs.",
    ],
    snippet: `{
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "[Attorney name]",
  "jobTitle": "[Partner / Associate Attorney]",
  "url": "[https://yourfirm.com/attorneys/name]",
  "image": "[https://yourfirm.com/images/attorney.jpg]",
  "worksFor": {
    "@type": "LegalService",
    "name": "[Firm name]",
    "url": "[https://yourfirm.com]"
  },
  "sameAs": [
    "[State bar profile URL]",
    "[Directory profile URL]"
  ]
}`,
  },
  {
    key: "location",
    label: "Location / city page",
    types: ["LegalService"],
    notes: [
      "Only for a real office. Name, address and phone should match your Google Business Profile exactly.",
      "Opening hours and the area served help Google connect the page to the right local searches.",
    ],
    snippet: `{
  "@context": "https://schema.org",
  "@type": "LegalService",
  "name": "[Firm name] – [City] office",
  "url": "[https://yourfirm.com/locations/city]",
  "telephone": "[+1-000-000-0000]",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "[Street]",
    "addressLocality": "[City]",
    "addressRegion": "[State]",
    "postalCode": "[ZIP]",
    "addressCountry": "US"
  },
  "openingHours": "[Mo-Fr 09:00-17:00]",
  "areaServed": "[City, County]"
}`,
  },
  {
    key: "faq",
    label: "Page with an FAQ section",
    types: ["FAQPage"],
    notes: [
      "Every question and answer in the markup must also be visible on the page, word for word.",
      "Google now shows FAQ rich results only for well-known government and health sites, so the markup is valid but a law firm shouldn't expect the expandable result.",
    ],
    snippet: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "[Question exactly as shown on the page]",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "[Answer exactly as shown on the page]"
      }
    }
  ]
}`,
  },
  {
    key: "post",
    label: "Blog post or other nested page",
    types: ["BlogPosting", "BreadcrumbList"],
    notes: [
      "Name the real author (the attorney) and use the actual published and modified dates.",
      "BreadcrumbList should match the breadcrumb readers see on the page.",
    ],
    snippet: `{
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": "[Post title]",
  "datePublished": "[YYYY-MM-DD]",
  "dateModified": "[YYYY-MM-DD]",
  "author": {
    "@type": "Person",
    "name": "[Attorney name]",
    "url": "[https://yourfirm.com/attorneys/name]"
  },
  "publisher": {
    "@type": "Organization",
    "name": "[Firm name]"
  }
}`,
  },
];

export default function SchemaPicker() {
  const [selected, setSelected] = useState<PageKey | null>(null);
  const [copyStatus, setCopyStatus] = useState("");
  const option = OPTIONS.find((o) => o.key === selected) ?? null;

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  const copy = async () => {
    if (!option) return;
    try {
      await navigator.clipboard.writeText(option.snippet);
      setCopyStatus("Copied. Replace every [placeholder] before using it.");
    } catch {
      setCopyStatus("Couldn't copy automatically. Select the code and copy it manually.");
    }
  };

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Which structured data does this page need?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Pick a page type for the markup that fits and a starter you can hand to whoever edits your site.
      </p>

      <div role="group" aria-label="Page type" className="mt-4 flex flex-wrap gap-2">
        {OPTIONS.map((o) => (
          <Button variant="outline"
            key={o.key}
            type="button"
            aria-pressed={selected === o.key}
            onClick={() => {
              setSelected(o.key);
              setCopyStatus("");
            }}
            className={`h-auto whitespace-normal rounded-md border px-3 py-1.5 text-sm ${focusRing} ${
              selected === o.key ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
            }`}
          >
            {o.label}
          </Button>
        ))}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {!option ? (
          <p className="text-sm text-muted-foreground">Choose a page type to see the markup it needs.</p>
        ) : (
          <div>
            <p className="text-sm font-semibold">
              Use: {option.types.join(" + ")}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              {option.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
            <p className="mt-4 text-sm font-medium">Starter JSON-LD (replace every [placeholder])</p>
            <pre className="mt-2 max-h-80 overflow-auto rounded-md border border-border bg-card p-3 text-xs">
              <code>{option.snippet}</code>
            </pre>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button variant="outline"
                type="button"
                onClick={copy}
                className={`rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ${focusRing}`}
              >
                Copy code
              </Button>
              {copyStatus && <span className="text-sm">{copyStatus}</span>}
            </div>
            <p className="mt-4 text-sm">
              Test it with Google's Rich Results Test before publishing.{" "}
              <Link to="/services/$slug" params={{ slug: "technical-seo-for-law-firms" }} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Technical SEO for law firms →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
